import { Injectable } from '@angular/core';
import {
    Action,
    createAction,
    props,
    Store
} from '@ngrx/store';
import { Observable } from 'rxjs';
import * as DatiqAppSelectors from '../datiq-app.selectors';
import * as DatiqAppActions from '../datiq-app.actions';
import { DatiqLinkService } from '@dat/datiq-utils';
import { Router } from '@angular/router';
import {
    DatiqAppConfig,
    DatiqEnvironments,
    IDatiqAction,
    IDatiqLink,
    IDatiqRoute,
    IDatiqRulesData,
    IDatiqUnavItem,
} from '@dat/datiq-core/models';

@Injectable({
    providedIn: 'root',
})
export class DatiqAppStoreService {
    constructor(
        private _store: Store,
        private _link: DatiqLinkService,
        private _router: Router
    ) {}

    isCurrentRouteUnlocked$(): Observable<boolean> {
        return this._store.select(DatiqAppSelectors.isCurrentRouteUnlocked());
    }

    lockCurrentRoute(): void {
        this._store.dispatch(DatiqAppActions.lockCurrentRoute());
    }

    unlockCurrentRoute(): void {
        this._store.dispatch(DatiqAppActions.unlockCurrentRoute());
    }

    loaded(config: DatiqAppConfig): void {
        this._store.dispatch(DatiqAppActions.loaded({ config }));
    }

    loading() {
        this._store.dispatch(DatiqAppActions.loading());
    }

    isEnvironment$(environment: DatiqEnvironments): Observable<boolean> {
        return this._store.select(DatiqAppSelectors.isEnvironment(environment));
    }

    getEnvironment$(): Observable<string> {
        return this._store.select(DatiqAppSelectors.getEnvironment());
    }

    getLinkRules$(): Observable<IDatiqRulesData<IDatiqUnavItem>[]> {
        return this._store.select(DatiqAppSelectors.getLinkRules());
    }

    getUserLinkRules$(): Observable<IDatiqRulesData<IDatiqUnavItem>[]> {
        return this._store.select(DatiqAppSelectors.getUserLinkRules());
    }

    getAppLoaded$(): Observable<boolean> {
        return this._store.select(DatiqAppSelectors.getDatiqAppLoaded);
    }

    initializeApp(): void {
        this._store.dispatch(DatiqAppActions.init());
    }

    /**
     * This function will take the settings for the action and will dispatch into the store.
     * The idea here is to use actions with effects in local stores to do the action rather than
     * trying to add listeners.
     * @param data - data for the action
     */
    fireAction(data: IDatiqAction): void {
        const action = this.createAction(data);
        this._store.dispatch(action);
    }

    createAction(data: IDatiqAction): Action {
        /**
         * Using any here for the props since we can't do unknown props
         * for actions.
         */
        return !!data?.payload
            ? createAction(data.name, props<any>())(data?.payload)
            : createAction(data.name)();
    }

    reroute(link: IDatiqLink): void {
        !!this._link.isRoute(link)
            ? this._router.navigate(link.route)
            : this._link.navigate(link);
    }

    setRoute(route: IDatiqRoute): void {
        this._store.dispatch(DatiqAppActions.setRoute({ route }));
    }

    isCurrentRoute$(value: string): Observable<boolean> {
        return this._store.select(DatiqAppSelectors.isCurrentRoute(value));
    }

    containsCurrentRoute$(value: string): Observable<boolean> {
        return this._store.select(
            DatiqAppSelectors.containsCurrentRoute(value)
        );
    }

    getCurrentRoute$(): Observable<IDatiqRoute> {
        return this._store.select(DatiqAppSelectors.getCurrentRoute());
    }
}
